import { Popover } from '@base-ui/react/popover';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useHeaderFlyoutStore } from '../../../../../shared';

export type AccountPopoverVariant = 'desktop' | 'tablet' | 'mobile';

type UseAccountPopoverOptions = {
    variant: AccountPopoverVariant;
};

type OnOpenChange = NonNullable<Popover.Root.Props['onOpenChange']>;

export function useAccountPopover({ variant }: UseAccountPopoverOptions) {
    const isDesktop = variant === 'desktop';
    const actionsRef = useRef<Popover.Root.Actions>(null);
    const toggleRef = useRef<HTMLButtonElement | null>(null);
    const setFlyoutOpen = useHeaderFlyoutStore(state => state.setFlyoutOpen);

    const [openedWithKeyboard, setOpenedWithKeyboard] = useState(false);
    const [mobileAnchor, setMobileAnchor] = useState<HTMLElement | null>(null);
    const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);

    const setPopoverRoot = useCallback((element: HTMLElement | null) => {
        setMobileAnchor(element?.closest<HTMLElement>('.header__main-section-container') ?? null);
        setPortalContainer(element?.closest<HTMLElement>('.header__main-section-sticky') ?? null);
    }, []);

    function close() {
        actionsRef.current?.close();
    }

    const onOpenChange: OnOpenChange = (open, eventDetails) => {
        if (isDesktop) {
            setFlyoutOpen('account', open);
        }

        if (!open || eventDetails.reason === 'trigger-hover') {
            setOpenedWithKeyboard(false);
        }

        if (!open && eventDetails.reason === 'escape-key') {
            requestAnimationFrame(() => toggleRef.current?.focus());
        }
    };

    useEffect(() => {
        if (!isDesktop) return;

        return () => setFlyoutOpen('account', false);
    }, [isDesktop, setFlyoutOpen]);

    return {
        close,
        isDesktop,
        mobileAnchor,
        portalContainer,
        rootProps: { actionsRef, onOpenChange },
        setPopoverRoot,
        triggerProps: {
            isDesktop,
            onClose: close,
            onKeyboardOpen: () => setOpenedWithKeyboard(true),
            openedWithKeyboard,
            toggleRef,
        },
    };
}
